import { useEffect, useRef } from 'react'
import { MicOff } from 'lucide-react'
import { useGlobalState } from '@highlight/ipc/react'
import { useIsTakingNotes } from '@renderer/hooks/audio/use-audio-note-selectors'
import { setMeetingMicCaptureOverride } from '@renderer/overlay/components/meeting-mic-status'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { MEETING_MIC_GATE_KEY, NEUTRAL_MEETING_MIC_GATE, type MeetingMicGate } from '@shared/types/meeting-mic-gate'
import { useToast } from '../use-toast'

const MERGED_MUTE_TOAST_ID = 'meeting-merged-mute-capture'

type ToastApi = ReturnType<typeof useToast>

/**
 * Merged-audio calls only: Meet routes the voice through a room device, so
 * respecting the mute captures nothing at all. Ordinary mute needs no toast —
 * mute-means-not-recorded is the model users already hold.
 */
export function useMicGateToasts(toast: ToastApi) {
  const [gate] = useGlobalState<MeetingMicGate>(MEETING_MIC_GATE_KEY, NEUTRAL_MEETING_MIC_GATE)
  const isTakingNotes = useIsTakingNotes()
  const shownRef = useRef(false)

  useEffect(() => {
    if (!isTakingNotes) {
      shownRef.current = false
      return
    }
    if (!gate.shouldDropMic || !gate.mergedAudio || shownRef.current) return

    shownRef.current = true
    AmplitudeRenderer.trackEvent('Meeting Mic Capture Toast', { reason: 'merged muted' })
    toast.add({
      analytics: { type: 'merged mute capture' },
      id: MERGED_MUTE_TOAST_ID,
      icon: <MicOff size={16} />,
      title: 'Muted — not recording',
      description:
        'This call shares audio with a nearby device, so while you’re muted Highlight can’t capture the room either.',
      duration: 15000,
      autoExpandDelayMs: 0,
      priority: 1,
      actions: [
        {
          variant: 'primary',
          children: 'Record anyway',
          onClick: () => setMeetingMicCaptureOverride(true),
        },
      ],
    })
  }, [gate.shouldDropMic, gate.mergedAudio, isTakingNotes, toast])
}
